import { useCallback } from 'react';
import * as PIXI from 'pixi.js';
import { useTranslation } from 'react-i18next';
import { getFurnitureName } from '../../data/furniture';
import { mirrorFpOffset, getTileDims } from '../../domain/tileMath';
import { toPlacementMode } from './interaction/placement';
import { getFurnitureDepthComponents, getOccupiedBaseTile } from '../../domain/depthMath';
import {
  snapWindowAnchorToGrid as snapWindowAnchorToGridImported,
  WINDOW_VERTICAL_OFFSET,
} from '../../domain/windowSnap';
import type { RoomSceneState } from './state/roomSceneState';
import { asFurnitureMarker, asFurnitureSprite, liveFurnitureData } from './render/pixiExtensions';

// ── Furniture diagnostics console dump ──
// Extracted verbatim from RoomCanvas (decomposition final cleanup pass). The
// callback body is byte-identical to the original; TILE_HEIGHT is received as a
// parameter (RoomCanvas owns the grid constants — same convention as
// useFurnitureRender), WINDOW_VERTICAL_OFFSET / snapWindowAnchorToGrid come from
// windowSnap directly (RoomCanvas's local WINDOW_VERTICAL_OFFSET was a straight
// re-export alias of the same constant), and `t` comes from useTranslation()
// here (same provider instance — matches useRoomActions / useFurnitureInteraction).

export interface UseFurnitureDiagnosticsParams {
  sceneRef: React.MutableRefObject<RoomSceneState>;
  TILE_HEIGHT: number;
}

export function useFurnitureDiagnostics({
  sceneRef,
  TILE_HEIGHT,
}: UseFurnitureDiagnosticsParams): () => void {
  const { t } = useTranslation();

  // ── Diagnostic log function: outputs detailed furniture data ──
  const logFurnitureDiagnostics = useCallback(() => {
    const rows: Record<string, unknown>[] = [];
    for (const [fId, marker] of sceneRef.current.furniture.markers.entries()) {
      const sprite = asFurnitureSprite(marker.children[0] as PIXI.Sprite | undefined);
      const fData = liveFurnitureData(sprite, asFurnitureMarker(marker));
      const catName = fData?.furniture_catalog ? getFurnitureName(fData.furniture_catalog.id, t) : '?';
      const { w: wT, h: hT, isRug: itemIsRug, isWindow: itemIsWindow } = getTileDims(fData?.furniture_catalog);
      // CRITICAL: For windows/wall-mounted, temporarily subtract WINDOW_VERTICAL_OFFSET from marker.y
      // before computing tile coordinates, because marker.y includes the offset but
      // getFurnitureDepthComponents expects floor-level Y for correct tile calculation.
      const isWindowItem = itemIsWindow;
      const origY = marker.position.y;
      if (isWindowItem) {
        marker.position.y = origY - WINDOW_VERTICAL_OFFSET;
      }
      const depth = getFurnitureDepthComponents(marker, wT, hT, itemIsRug);
      if (isWindowItem) {
        marker.position.y = origY; // Restore
      }
      const isMirrored = !!(fData?.is_mirrored);
      // For windows use snap for correct baseTile; for others use getOccupiedBaseTile
      let baseTileX: number, baseTileY: number;
      if (isWindowItem) {
        const floorY = origY - WINDOW_VERTICAL_OFFSET;
        const snapR = snapWindowAnchorToGridImported(
          marker.position.x, floorY,
          depth.effectiveWidthTiles, depth.effectiveHeightTiles,
        );
        baseTileX = snapR.baseTileX;
        baseTileY = snapR.baseTileY;
      } else {
        const base = getOccupiedBaseTile(depth, isMirrored);
        baseTileX = base.baseTileX;
        baseTileY = base.baseTileY;
      }
      const spriteW = sprite ? Math.abs(sprite.width) : 0;
      const spriteH = sprite ? Math.abs(sprite.height) : 0;
      // Compute max footprint-tile depth (front-most tile) for items with footprint_tiles
      const fpTiles = fData?.furniture_catalog?.footprint_tiles;
      let maxFpDepth: number | 'n/a' = 'n/a';
      if (fpTiles && fpTiles.length > 0) {
        const halfTH = TILE_HEIGHT / 2;
        const ctX = Math.round(depth.tileX);
        const ctY = Math.round(depth.tileY);
        let maxD = -Infinity;
        for (const [dx, dy] of fpTiles) {
          const [tdx, tdy] = mirrorFpOffset(dx, dy, isMirrored);
          const d = (ctX + tdx + ctY + tdy) * halfTH;
          if (d > maxD) maxD = d;
        }
        maxFpDepth = Number(maxD.toFixed(2));
      }
      rows.push({
        id: fId,
        name: catName,
        category: fData?.furniture_catalog?.category ?? '?',
        worldX: Number(marker.position.x.toFixed(1)),
        worldY: Number(marker.position.y.toFixed(1)),
        tileX: Number(depth.tileX.toFixed(2)),
        tileY: Number(depth.tileY.toFixed(2)),
        frontTileX: Number(depth.frontTileX.toFixed(2)),
        frontTileY: Number(depth.frontTileY.toFixed(2)),
        backendW: wT ?? 'null',
        backendH: hT ?? 'null',
        effectiveW: depth.effectiveWidthTiles,
        effectiveH: depth.effectiveHeightTiles,
        baseTileX,
        baseTileY,
        depthBase: Number(depth.depthBase.toFixed(1)),
        depthFpOffset: Number(depth.depthFootprintOffset.toFixed(1)),
        maxFpDepth,
        depth: Number(depth.depth.toFixed(4)),
        zIndex: Number(marker.zIndex.toFixed(4)),
        placement_mode: toPlacementMode(fData?.placement_mode),
        spriteW: Number(spriteW.toFixed(1)),
        spriteH: Number(spriteH.toFixed(1)),
        mirrored: isMirrored,
      });
    }
    rows.sort((a, b) => (a.zIndex as number) - (b.zIndex as number));
    const ts = new Date().toISOString().slice(11, 23);
    console.group(`[RoomCanvas] Furniture diagnostics @ ${ts} (${rows.length} items, sorted by zIndex)`);
    console.table(rows);
    console.groupEnd();
    // Викликається імперативно (кнопка/рендер-пас), не як effect.
    // sceneRef/TILE_HEIGHT стабільні; t з i18n — міг би змінюватись, але dump
    // читає t на момент кліку (свіжий через closure re-create при t change).
  }, [sceneRef, TILE_HEIGHT, t]);

  return logFurnitureDiagnostics;
}
